import * as React from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { cn, Button, Textarea } from '@ai-content-hub/content-kit';
import { Paperclip, Send } from 'lucide-react';
import type { PromptFormApiConfig, UploadedDocument } from '../../types';

const MAX_LENGTH = 3000;

export interface ChatInputProps {
    onSend: (text: string, documents?: UploadedDocument[]) => void;
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
    apiConfig?: PromptFormApiConfig;
    className?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
    onSend,
    disabled = false,
    loading = false,
    placeholder = 'Nhập tin nhắn của bạn...',
    apiConfig,
    className,
}) => {
    const [text, setText] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [documents, setDocuments] = useState<UploadedDocument[]>([]);

    const canSend = text.trim().length > 0 || documents.length > 0;
    const isDisabled = disabled || loading;

    const resizeTextarea = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, []);

    useEffect(() => { resizeTextarea(); }, [text, resizeTextarea]);

    const handleSend = useCallback(() => {
        if (!canSend || isDisabled) return;
        onSend(text.trim(), documents.length > 0 ? documents : undefined);
        setText('');
        setDocuments([]);
    }, [canSend, isDisabled, onSend, text, documents]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend],
    );

    const handleFileUpload = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file || !apiConfig) return;

            try {
                const formData = new FormData();
                formData.append('file', file);

                const headers: Record<string, string> = {};
                const token = apiConfig.getAuthToken?.();
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const res = await fetch(`${apiConfig.baseUrl}/api/upload`, {
                    method: 'POST',
                    headers,
                    body: formData,
                });
                const data = await res.json();

                setDocuments((prev) => [
                    ...prev,
                    {
                        id: data.key || crypto.randomUUID(),
                        name: file.name,
                        url: data.url,
                        size: file.size,
                    },
                ]);
            } catch {
                // upload failure is non-fatal
            }
            e.target.value = '';
        },
        [apiConfig],
    );

    return (
        <div className={cn('chat-input border-t border-border/60 p-3', className)}>
            {documents.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {documents.map((doc) => (
                        <span
                            key={doc.id}
                            className="chat-input-doc inline-flex items-center gap-1 text-xs bg-muted/50 rounded px-2 py-0.5 text-muted-foreground"
                        >
                            {doc.name}
                            <button
                                type="button"
                                className="hover:text-destructive cursor-pointer"
                                onClick={() => setDocuments((d) => d.filter((x) => x.id !== doc.id))}
                            >
                                &times;
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="flex items-end gap-2">
                {apiConfig && (
                    <>
                        <button
                            type="button"
                            className={cn(
                                'chat-input-attach flex-shrink-0 p-2 rounded-lg',
                                'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                                'transition-colors cursor-pointer',
                                isDisabled && 'opacity-50 pointer-events-none',
                            )}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isDisabled}
                        >
                            <Paperclip className="h-4 w-4" />
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.txt,.doc,.docx,.rtf"
                            className="hidden"
                            onChange={handleFileUpload}
                        />
                    </>
                )}

                <div className="flex-1 flex" style={{ position: 'relative' }}>
                    <Textarea
                        ref={textareaRef}
                        value={text}
                        onChange={(e) => setText(e.target.value.slice(0, MAX_LENGTH))}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        disabled={isDisabled}
                        className="chat-input-textarea min-h-[40px] max-h-[120px] resize-none text-sm flex-1 overflow-y-auto pr-12"
                        rows={1}
                    />
                    <span
                        style={{
                            position: 'absolute',
                            right: 15,
                            bottom: 3,
                            display: text.length >= MAX_LENGTH ? 'block' : 'none',
                            color: text.length >= MAX_LENGTH ? 'red' : 'gray',
                        }}
                    >
                        {text.length}/{MAX_LENGTH}
                    </span>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button
                        size="icon"
                        onClick={handleSend}
                        disabled={!canSend || isDisabled}
                        className="chat-input-send h-9 w-9 rounded-lg"
                    >
                        <Send className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};
